import { Button, TrashIcon } from '@quickmd-llc/design-system'
import { VisitTypeForm, type VisitTypeFormState } from '../visit-type-form/VisitTypeForm'
import { VisitTypeViewMode } from '../visit-type-view-mode/VisitTypeViewMode'
import type { ViewServiceVisitType } from '../../types'

type IntakeFormItem = { IntakeFormId?: string | null; Name?: string | null }
type ConsentFormItem = { ConsentFormId?: string | null; Name?: string | null }

type Props = {
  isEditMode: boolean
  serviceTypes: ViewServiceVisitType[]
  /** Setter exposed to VisitTypeForm; the orchestrator adapts it back to its working type. */
  setServiceTypes: (types: VisitTypeFormState[]) => void
  intakeForms: IntakeFormItem[]
  additionalConsentFormsOptions: ConsentFormItem[]
  onRequestDeleteVisitType: (visitTypeId: string | null) => void
}

/**
 * The visit-type list: in edit mode a stack of editable cards (each wrapping VisitTypeForm with a
 * per-card delete button), in view mode the read-only VisitTypeViewMode cards.
 *
 * SCSS → Tailwind mapping ($card-gap-color = var(--border-card) = shade[10]):
 *   .vs-visit-types -> flex flex-col gap-sp-2
 *   .ns-visit-type-card -> bg-shade-10 border border-shade-10 rounded-large overflow-hidden
 *                          flex flex-col gap-px   (gap-px exposes the dark bg between white panels)
 *   .ns-card-header -> bg-white py-sp-2.5 px-sp-3 flex items-center justify-between
 *   .ns-card-title  -> text-size-5 font-semibold leading-7 text-text-dark
 *   .ns-visit-type-delete-btn:hover -> hover:text-text-error on button + svg
 *
 * The pw_visit-type-card testid is preserved on each edit-mode card (E2E depends on it).
 */
export const VisitTypesSection = ({
  isEditMode,
  serviceTypes,
  setServiceTypes,
  intakeForms,
  additionalConsentFormsOptions,
  onRequestDeleteVisitType,
}: Props) => (
  <div className='flex flex-col gap-sp-2'>
    {serviceTypes.map((t) =>
      isEditMode ?
        <div
          key={t.VisitTypeId}
          data-testid='pw_visit-type-card'
          className='flex flex-col gap-px overflow-hidden rounded-large border border-shade-10 bg-shade-10'>
          <div className='flex items-center justify-between bg-white px-sp-3 py-sp-2.5'>
            <span className='text-size-5 font-semibold leading-7 text-text-dark'>Visit type</span>
            {serviceTypes.length > 1 && (
              <div className='[&_button:hover]:text-text-error [&_button:hover_svg]:text-text-error'>
                <Button priority='tertiary' size='s' onClick={() => onRequestDeleteVisitType(t.VisitTypeId)}>
                  <TrashIcon iconSize='s' />
                  Delete
                </Button>
              </div>
            )}
          </div>
          <VisitTypeForm
            serviceTypeConfig={t}
            serviceTypes={serviceTypes}
            setServiceTypes={setServiceTypes}
            intakeForms={intakeForms}
            additionalConsentFormsOptions={additionalConsentFormsOptions}
          />
        </div>
      : <VisitTypeViewMode key={t.VisitTypeId} currentVisitTypeId={t.VisitTypeId ?? ''} />,
    )}
  </div>
)
